import * as React from 'react';
import { FunctionComponent } from 'react';
import propz from 'propz';
import { getFullName } from 'Src/helpers/autocomplete/autocomplete';
import { StarRatingBar } from '../StarRatingBar/StarRatingBar';
import {
  SchoolEvent,
  SchoolEventIndividualData,
  SchoolEventIndividualPerformance,
  SchoolEventTeamDataPlayer
} from '../../models/event';
import { isClubEvent } from '../../helpers/event/event';
import './SchoolEventPerformanceSummary.scss';

interface Props {
  schoolEvent: SchoolEvent;
  allTeamPlayers: (SchoolEventIndividualData | SchoolEventTeamDataPlayer)[];
  individualPerformance: SchoolEventIndividualPerformance[];
  onEditEventPerformanceClick: (value, player, onePerformanceId) => void;
  onEditPerformanceClick?: () => void;
  onSavePerformanceClick?: () => void;
  isEditPerformance: boolean;
}

export const SchoolEventPerformanceSummary: FunctionComponent<Props> = props => {
  const { schoolEvent, allTeamPlayers, individualPerformance, isEditPerformance } = props;
  const sportPerformanceArray = propz.get(schoolEvent, ['sport', 'performance'], []);
  const columnPerformanceName = isClubEvent(schoolEvent) ? 'Progress' : 'Performance';

  const getInitials = (name: string) =>
    name
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map(namePart => namePart.charAt(0).toUpperCase())
      .join('');

  const getAvatarUrl = (player: SchoolEventIndividualData | SchoolEventTeamDataPlayer) => {
    return propz.get(player, ['avatar'], '');
  };

  const getPlayerPerformanceValue = (
    player: SchoolEventIndividualData | SchoolEventTeamDataPlayer,
    performanceId: string
  ) => {
    const pData = individualPerformance.find(
      pUserData => pUserData.performanceId === performanceId && pUserData.userId === player.userId
    );
    return pData ? pData.value : 0;
  };

  const getCompletedPlayersCount = () => {
    return allTeamPlayers.filter(player =>
      sportPerformanceArray.some(performance => getPlayerPerformanceValue(player, performance._id) > 0)
    ).length;
  };

  const renderAvatar = (player: SchoolEventIndividualData | SchoolEventTeamDataPlayer, name: string) => {
    const avatar = getAvatarUrl(player);
    return (
      <span className="eEventMeasuresAvatar">
        {avatar ? (
          <img className="eEventMeasuresAvatar__img" src={avatar} alt="" />
        ) : (
          <span className="eEventMeasuresAvatar__fallback">{getInitials(name)}</span>
        )}
      </span>
    );
  };

  const renderPlayerPerformance = player => {
    return sportPerformanceArray.map(onePerformance => {
      const value = getPlayerPerformanceValue(player, onePerformance._id);

      const minValue = propz.get(onePerformance, ['minValue'], 0);
      const maxValue = propz.get(onePerformance, ['maxValue'], 0);

      return (
        <div key={onePerformance._id} className="eEventMeasureItem">
          <div className="eEventMeasureItem__copy">
            <div className="eEventMeasureItem__name">{onePerformance.name}</div>
            <div className="eEventMeasureItem__range">
              {minValue} - {maxValue}
            </div>
          </div>
          <div className="eEventMeasureItem__control eEventMeasureItem__control--rating">
            <StarRatingBar
              minValue={minValue}
              maxValue={maxValue}
              player={player}
              onePerformanceId={onePerformance._id}
              isEditMode={isEditPerformance}
              value={value}
              handleValueChanges={props.onEditEventPerformanceClick}
            />
          </div>
        </div>
      );
    });
  };

  const rows = allTeamPlayers.map((player, index) => {
    const fullName = getFullName(player);
    return (
      <div key={`performance_summary_${index}`} className="eEventMeasuresRow">
        <div className="eEventMeasuresRow__student">
          <span className="eEventMeasuresRow__index">{index + 1}</span>
          {renderAvatar(player, fullName)}
          <div className="eEventMeasuresRow__studentCopy">
            <div className="eEventMeasuresRow__name">{fullName}</div>
          </div>
        </div>
        <div className="eEventMeasuresRow__metrics">{renderPlayerPerformance(player)}</div>
      </div>
    );
  });

  return (
    <div className="eEventMeasures">
      <div className="eEventMeasuresHeader">
        <div>
          <h2 className="eEventMeasuresHeader__title">{columnPerformanceName}</h2>
          <div className="eEventMeasuresHeader__subtitle">Review each participant's event development ratings.</div>
        </div>
        <div className="eEventMeasuresHeader__actions">
          {isEditPerformance ? (
            <button
              type="button"
              className="sit-btn sit-btn--primary sit-btn--sm"
              onClick={props.onSavePerformanceClick}
              disabled={!props.onSavePerformanceClick}
            >
              {isClubEvent(schoolEvent) ? 'Save progress' : 'Save performance'}
            </button>
          ) : (
            <button
              type="button"
              className="sit-btn sit-btn--primary sit-btn--sm"
              onClick={props.onEditPerformanceClick}
              disabled={!props.onEditPerformanceClick || allTeamPlayers.length === 0}
            >
              {isClubEvent(schoolEvent) ? 'Edit progress' : 'Edit performance'}
            </button>
          )}
        </div>
      </div>

      <div className="eEventMeasuresSummary">
        <div className="eEventMeasuresSummaryChip eEventMeasuresSummaryChip--total">
          <span className="eEventMeasuresSummaryChip__dot" />
          <span className="eEventMeasuresSummaryChip__label">Participants</span>
          <span className="eEventMeasuresSummaryChip__value">{allTeamPlayers.length}</span>
        </div>
        <div className="eEventMeasuresSummaryChip eEventMeasuresSummaryChip--metrics">
          <span className="eEventMeasuresSummaryChip__dot" />
          <span className="eEventMeasuresSummaryChip__label">Metrics</span>
          <span className="eEventMeasuresSummaryChip__value">{sportPerformanceArray.length}</span>
        </div>
        <div className="eEventMeasuresSummaryChip eEventMeasuresSummaryChip--complete">
          <span className="eEventMeasuresSummaryChip__dot" />
          <span className="eEventMeasuresSummaryChip__label">Rated</span>
          <span className="eEventMeasuresSummaryChip__value">{getCompletedPlayersCount()}</span>
        </div>
      </div>

      <div className="eEventMeasuresList">
        {rows.length > 0 ? rows : <div className="eEventMeasuresEmpty">No participants</div>}
      </div>
    </div>
  );
};

SchoolEventPerformanceSummary.displayName = 'SchoolEventPerformanceSummary';
